import {
  hasSubscriptionAccess,
  SubscriptionLevel,
} from "./subscriptionLevels";

/**
 * App sections and minimum subscription tier.
 * Derived from backend [RequiredPackage] attributes, App.tsx SubscriptionLevelGate,
 * and IftaReportOptions.AllowedPlanCodes (Pro by default).
 */
export interface PlanSection {
  id: string;
  label: string;
  hub: "fleet" | "safety" | "accounting" | "admin" | "platform";
  requiredLevel: SubscriptionLevel;
}

export const PLAN_SECTIONS: PlanSection[] = [
  // Fleet
  {
    id: "dashboard",
    label: "Dashboard & live map",
    hub: "fleet",
    requiredLevel: "Lite",
  },
  { id: "shops", label: "Shops", hub: "fleet", requiredLevel: "Lite" },
  { id: "vehicles", label: "Vehicles", hub: "fleet", requiredLevel: "Lite" },
  { id: "trailers", label: "Trailers", hub: "fleet", requiredLevel: "Lite" },
  {
    id: "maintenance",
    label: "Maintenance",
    hub: "fleet",
    requiredLevel: "Lite",
  },
  {
    id: "pti",
    label: "Pre-Trip Inspections",
    hub: "fleet",
    requiredLevel: "Standard",
  },

  // Safety
  {
    id: "documents",
    label: "Documents",
    hub: "safety",
    requiredLevel: "Lite",
  },
  {
    id: "claims",
    label: "Claims Board",
    hub: "safety",
    requiredLevel: "Lite",
  },
  {
    id: "safety-board",
    label: "Safety Board",
    hub: "safety",
    requiredLevel: "Lite",
  },
  { id: "users", label: "Users & drivers", hub: "safety", requiredLevel: "Lite" },
  {
    id: "driver-assignments",
    label: "Driver assignments",
    hub: "safety",
    requiredLevel: "Standard",
  },

  // Accounting
  {
    id: "detention-lumper",
    label: "Detention / Lumper",
    hub: "accounting",
    requiredLevel: "Lite",
  },
  {
    id: "ifta",
    label: "IFTA Report",
    hub: "accounting",
    requiredLevel: "Pro",
  },

  // Admin
  {
    id: "integrations",
    label: "Integrations (Samsara, Motive, EvaEld)",
    hub: "admin",
    requiredLevel: "Lite",
  },
  {
    id: "share-links",
    label: "Share links",
    hub: "admin",
    requiredLevel: "Lite",
  },
  {
    id: "audit",
    label: "Audit log",
    hub: "admin",
    requiredLevel: "Lite",
  },
  {
    id: "absences",
    label: "Absences",
    hub: "admin",
    requiredLevel: "Standard",
  },
  {
    id: "truck-driver-portal",
    label: "Truck driver portal",
    hub: "admin",
    requiredLevel: "Standard",
  },
];

const HUB_LABELS: Record<PlanSection["hub"], string> = {
  fleet: "Fleet",
  safety: "Safety",
  accounting: "Accounting",
  admin: "Admin",
  platform: "Platform",
};

const LEVEL_ORDER: Record<SubscriptionLevel, number> = {
  Lite: 1,
  Standard: 2,
  Pro: 3,
};

/** Maps plan code from API/Stripe to subscription tier. */
export function planCodeToLevel(planCode: string | null | undefined): SubscriptionLevel | null {
  if (!planCode) return null;
  switch (planCode.trim().toUpperCase()) {
    case "LITE":
      return "Lite";
    case "STANDARD":
    case "BASIC":
      return "Standard";
    case "PRO":
      return "Pro";
    default:
      return null;
  }
}

/** Sections included in a plan (cumulative by tier). */
export function getIncludedSectionsForPlan(
  planCode: string | null | undefined,
): PlanSection[] {
  const tier = planCodeToLevel(planCode);
  if (!tier) return [];
  return PLAN_SECTIONS.filter((section) =>
    hasSubscriptionAccess(tier, section.requiredLevel),
  );
}

/** Sections that unlock at this exact tier (not in lower tiers). */
export function getSectionsUnlockedAtPlan(
  planCode: string | null | undefined,
): PlanSection[] {
  const tier = planCodeToLevel(planCode);
  if (!tier) return [];
  const tierOrder = LEVEL_ORDER[tier];
  return PLAN_SECTIONS.filter(
    (section) => LEVEL_ORDER[section.requiredLevel] === tierOrder,
  );
}

export interface GroupedPlanSections {
  hub: PlanSection["hub"];
  hubLabel: string;
  sections: PlanSection[];
}

/** Included sections grouped by hub for display. */
export function getGroupedSectionsForPlan(
  planCode: string | null | undefined,
): GroupedPlanSections[] {
  const included = getIncludedSectionsForPlan(planCode);
  const hubOrder: PlanSection["hub"][] = [
    "fleet",
    "safety",
    "accounting",
    "admin",
    "platform",
  ];

  return hubOrder
    .map((hub) => ({
      hub,
      hubLabel: HUB_LABELS[hub],
      sections: included.filter((s) => s.hub === hub),
    }))
    .filter((group) => group.sections.length > 0);
}
